/**
 * THE ONE WAY TO TOUCH WEB STORAGE on app.* and book.*.
 *
 * Safari's private mode, blocked site data and some embedded webviews do not
 * merely return null from storage: the `localStorage` ACCESSOR itself throws,
 * and so can `getItem`, `setItem` (quota) and `removeItem`. Every caller had
 * written its own try/catch around that, five times over; this is the shared
 * version, and `npm run quality:contracts` (scripts/lib/one-way-contracts.js)
 * refuses a direct `localStorage`/`sessionStorage` reference anywhere else.
 *
 * Nothing here throws. A read that fails is `null`, exactly as a missing key
 * is; a write or removal that fails is `false`. Callers that must tell
 * "absent" from "unreadable" (the analytics consent record does) take the
 * `Storage` itself from `handle()` and keep their own try around the call.
 *
 * Keys are the callers' own and unchanged. Storage is per ORIGIN, so app.* and
 * book.* never see each other's values whatever the key.
 */

export type StorageArea = "local" | "session";

export interface SafeStorageArea {
  /** The `Storage` object, or null where it is absent or touching it throws. */
  handle(): Storage | null;
  /** The stored string, or null when missing, unavailable or throwing. */
  get(key: string): string | null;
  /** Whether the value was stored. Never throws. */
  set(key: string, value: string): boolean;
  /** Whether the removal ran. Never throws. */
  remove(key: string): boolean;
  /** The stored keys that start with `prefix`; none when unavailable or throwing. */
  keys(prefix: string): string[];
}

function area(name: StorageArea): SafeStorageArea {
  const handle = (): Storage | null => {
    try {
      // `globalThis`, not `window`: the same object in a browser, and still
      // defined (without storage) under Node.
      const store = name === "local" ? globalThis.localStorage : globalThis.sessionStorage;
      return store ?? null;
    } catch {
      return null;
    }
  };
  return {
    handle,
    get(key) {
      try {
        return handle()?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        const store = handle();
        if (!store) return false;
        store.setItem(key, value);
        return true;
      } catch {
        return false;
      }
    },
    remove(key) {
      try {
        const store = handle();
        if (!store) return false;
        store.removeItem(key);
        return true;
      } catch {
        return false;
      }
    },
    keys(prefix) {
      try {
        const store = handle();
        if (!store) return [];
        const found: string[] = [];
        for (let i = 0; i < store.length; i++) {
          const key = store.key(i);
          if (key !== null && key.startsWith(prefix)) found.push(key);
        }
        return found;
      } catch {
        return [];
      }
    },
  };
}

export const safeStorage: Readonly<Record<StorageArea, SafeStorageArea>> = Object.freeze({
  local: area("local"),
  session: area("session"),
});
